import { useValuationFormStore, ValuationFormValues } from '@/store/valuationFormStore';
import React, { createContext, useState, useContext, useMemo, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useGlobalFormContext } from './FormContext';
import { useLiveFormData } from '@/hooks/useLiveFormData';

// Define TypeScript interfaces for data structures
interface Property {
  upi: string;
  location: {
    village: string;
    cell: string;
    sector: string;
    district: string;
    city: string;
  };
  area: number;
  zone: string;
  improvements: {
    mainHouse: number;
    annex: number;
    fence: number;
    retainingWall: number;
    entranceGate: number;
    pavedArea: number;
    septicTank: boolean;
    landscaping: boolean;
  };
  constructionDetails: {
    roof: string;
    walls: string;
    doorsWindows: string;
    floor: string;
    rooms: {
      bedrooms: number;
      bathrooms: number;
      kitchen: number;
      sittingRoom: number;
      diningRoom: number;
      store: number;
    };
  };
}

interface ValuationInputs {
  landRate?: number;
  mainHouseRate?: number;
  annexRate?: number;
  fenceRate?: number;
  retainingWallRate?: number;
  entranceGateRate?: number;
  pavedAreaRate?: number;
  septicTankValue?: number;
  landscapingValue?: number;
  depreciationRate?: number;
  forcedSaleDiscount?: number;
}

interface ValuationResults {
  marketValue: number;
  insuranceValue: number;
  forcedSaleValue: number;
  improvementsValue: number;
  landValue: number;
}

interface ValuationContextType {
  property: Property;
  valuationInputs: ValuationInputs;
  valuationResults: ValuationResults;
  setValuationInputs: React.Dispatch<React.SetStateAction<ValuationInputs>>;
  formatCurrency: (amount: number | undefined) => string;
}

const ValuationContext = createContext<ValuationContextType | undefined>(undefined);

interface ValuationProviderProps {
  children: ReactNode;
}

export const ValuationProvider: React.FC<ValuationProviderProps> = ({ children }) => {


  const {  combinedData } = useLiveFormData();
  
  const [property] = useState<Property>({
    upi: '',
    location: {
      village: '',
      cell: '',
      sector: '',
      district: '',
      city: '',
    },
    area: 0, // in m²
    zone: '',
    improvements: {
      mainHouse: 0,
      annex: 0,
      fence: 0,
      retainingWall: 0,
      entranceGate: 0,
      pavedArea: 0,
      septicTank: true,
      landscaping: true,
    },
    constructionDetails: {
      roof: '',
      walls: '',
      doorsWindows: '',
      floor: '',
      rooms: {
        bedrooms: 0,
        bathrooms: 0,
        kitchen: 0,
        sittingRoom: 0,
        diningRoom: 0,
        store: 0,
      },
    },
  });

  const [valuationInputs, setValuationInputs] = useState<ValuationInputs>({
    landRate: undefined,
    mainHouseRate: undefined,
    annexRate: undefined,
    fenceRate: undefined,
    retainingWallRate: undefined,
    entranceGateRate: undefined,
    pavedAreaRate: undefined,
    septicTankValue: undefined,
    landscapingValue: undefined,
    depreciationRate: undefined,
    forcedSaleDiscount: undefined,
  });

  const calculateValuation = (): ValuationResults => {
    // Ensure all inputs default to 0 if undefined
    const mainHouseValue =
      combinedData.mainHouseArea *
      (combinedData.mainHouseRate ?? 0) *
      (1 - (combinedData.depreciationRate ?? 0));

    const annexValue =
      combinedData.annexHouseArea *
      (combinedData.annexRate ?? 0) *
      (1 - (combinedData.depreciationRate ?? 0));

    const fenceValue =
      combinedData.gateFenceLength *
      (combinedData.fenceRate ?? 0) *
      (1 - (combinedData.depreciationRate ?? 0));

    const retainingWallValue =
      combinedData.retainingWallsLength *
      (combinedData.retainingWallRate ?? 0) *
      (1 - (combinedData.depreciationRate ?? 0));
      
    const entranceGateValue =
      property.improvements.entranceGate *
      (combinedData.entranceGateRate ?? 0) *
      (1 - (combinedData.depreciationRate ?? 0));

    const pavedAreaValue =
      combinedData.pavedArea *
      (combinedData.pavedAreaRate ?? 0) *
      (1 - (combinedData.depreciationRate ?? 0));

    let improvementsValue =
      (mainHouseValue || 0) +
      (annexValue || 0) +
      (fenceValue || 0) +
      (retainingWallValue || 0) +
      (entranceGateValue || 0) +
      (pavedAreaValue || 0);

    if (property.improvements.septicTank)
      improvementsValue += combinedData.septicTankValue ?? 0;
    if (property.improvements.landscaping)
      improvementsValue += combinedData.landscapingValue ?? 0;

    const landValue = combinedData.landArea * (combinedData.landRate ?? 0);
    const marketValue = improvementsValue + landValue;
    const insuranceValue = improvementsValue * 0.9 + landValue * 0.8;
    const forcedSaleValue = marketValue * (1 - (combinedData.forcedSaleDiscount ?? 0));

    return {
      marketValue: Math.round(marketValue || 0),
      insuranceValue: Math.round(insuranceValue || 0),
      forcedSaleValue: Math.round(forcedSaleValue || 0),
      improvementsValue: Math.round(improvementsValue || 0),
      landValue: Math.round(landValue || 0),
    };
  };

  const valuationResults = useMemo(() => calculateValuation(), [combinedData, valuationInputs]);

  const formatCurrency = (amount: number | undefined): string => {
    if (amount === undefined || isNaN(amount)) {
      return 'RWF 0';
    }
    return new Intl.NumberFormat('rw-RW', {
      style: 'currency',
      currency: 'RWF',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const value: ValuationContextType = {
    property,
    valuationInputs,
    valuationResults,
    setValuationInputs,
    formatCurrency,
  };

  return <ValuationContext.Provider value={value}>{children}</ValuationContext.Provider>;
};

export const useValuation = () => {
  const context = useContext(ValuationContext);
  if (!context) {
    throw new Error('useValuation must be used within a ValuationProvider');
  }
  return context;
};